/**
 * Create a staff account (design 2B): Auth employee onboarding — Zoyaar
 * generates the temp password, shown EXACTLY ONCE in the handover panel.
 * The panel also carries the honest ACCOUNT_NOT_READY limitation note.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Machine, Notice, TextField } from '@/ui/kit';
import { FieldGrid, FormCard, SelectField, Span2 } from '@/ui/formkit';
import { addEmployee, listRoles, type AddEmployeeResult } from '@/core/auth/adminApi';
import { isE164, normalizeIranMobile } from '@/core/auth/phone';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { ApiError, NetworkError } from '@/core/api/errors';

/**
 * ZOY-375: Auth refuses onboarding with English developer prose ("…manage
 * their roles via the RBAC endpoints"). Known refusals map to clinic-readable
 * copy in both languages; endpoint names, RBAC terminology and internal codes
 * never render. Matching is on the message because these refusals carry no
 * typed code (only NATIONAL_ID_CONFLICT arrives as a "CODE: text" prefix).
 */
export function staffCreateErrorKey(err: unknown): string {
  if (err instanceof NetworkError) return 'signin.netBody';
  if (err instanceof ApiError) {
    const m = err.serverMessage ?? '';
    if (err.authCode === 'NATIONAL_ID_CONFLICT') return 'staffNew.errNationalId';
    if (/suspended member/i.test(m)) return 'staffNew.errSuspended';
    if (/already a member|already holds a live membership/i.test(m))
      return 'staffNew.errAlreadyMember';
    if (/still being provisioned/i.test(m)) return 'staffNew.errProvisioning';
    if (/exists but is disabled/i.test(m)) return 'staffNew.errDisabled';
    if (err.status === 400) return 'staffNew.errRefused';
  }
  return 'common.failedKept';
}

/**
 * tasks#142: a mobile that already belongs to an account is never attached
 * silently. Auth answers 409 EXISTING_ACCOUNT_CONFIRMATION_REQUIRED with a
 * masked name («س*** م***», or null) and a token; the admin checks the name
 * and confirms, and the same request is repeated with the token.
 */
export interface ExistingAccountConfirmation {
  maskedName: string | null;
  token: string;
}

export function existingAccountConfirmation(err: unknown): ExistingAccountConfirmation | null {
  if (!(err instanceof ApiError) || err.authCode !== 'EXISTING_ACCOUNT_CONFIRMATION_REQUIRED')
    return null;
  const body = (err.body ?? {}) as Record<string, unknown>;
  if (typeof body.confirmExistingToken !== 'string') return null;
  return {
    maskedName: typeof body.maskedName === 'string' ? body.maskedName : null,
    token: body.confirmExistingToken,
  };
}

export function StaffCreatePage() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const navigate = useNavigate();
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ['roles', clinicId],
    queryFn: () => listRoles(clinicId),
    staleTime: 60_000,
  });
  const roles = (rolesQuery.data ?? []).filter((r) => r.active !== false);

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [result, setResult] = useState<AddEmployeeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<
    (ExistingAccountConfirmation & { mobileE164: string }) | null
  >(null);

  const create = useMutation({
    mutationFn: (args: { mobileE164: string; confirmExistingToken?: string }) =>
      addEmployee(clinicId, {
        mobile: args.mobileE164,
        firstName: first.trim(),
        lastName: last.trim(),
        roleIds: [roleId],
        email: email.trim() || undefined,
        confirmExistingToken: args.confirmExistingToken,
      }),
    onSuccess: (res) => {
      setConfirm(null);
      setResult(res);
      void queryClient.invalidateQueries({ queryKey: ['members', clinicId] });
    },
    onError: (err, args) => {
      const needed = existingAccountConfirmation(err);
      if (needed) {
        setConfirm({ ...needed, mobileE164: args.mobileE164 });
        return;
      }
      setConfirm(null);
      // Typed values stay in place — only the message changes (ZOY-375).
      setError(t(staffCreateErrorKey(err)));
    },
  });

  if (result) {
    return (
      <HandoverPanel
        result={result}
        email={email.trim()}
        onDone={() => void navigate({ to: '/clinic-setup/staff' })}
      />
    );
  }

  const ready = first.trim() !== '' && last.trim() !== '' && mobile.trim() !== '' && roleId !== '';

  const submit = () => {
    setError(null);
    const mobileE164 = normalizeIranMobile(mobile);
    if (!isE164(mobileE164)) {
      setError(t('staffNew.mobileInvalid'));
      return;
    }
    setConfirm(null);
    create.mutate({ mobileE164 });
  };

  // Editing the number withdraws a pending confirmation: it was for that number only.
  const changeMobile = (value: string) => {
    setMobile(value);
    setConfirm(null);
  };

  return (
    <div style={{ maxInlineSize: 680, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={a.pageHead} style={{ marginBlockEnd: 0 }}>
        <h1 className={a.pageTitle}>{t('staffNew.title')}</h1>
        <p className={a.pageSub}>{t('staffNew.sub')}</p>
      </div>
      {error && (
        <Notice kind="err" role="alert">
          {error}
        </Notice>
      )}
      {confirm && (
        <Notice
          kind="warn"
          role="alert"
          title={t('staffNew.confirmTitle')}
          action={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button
                variant="brand"
                busy={create.isPending}
                disabled={create.isPending}
                onClick={() =>
                  create.mutate({
                    mobileE164: confirm.mobileE164,
                    confirmExistingToken: confirm.token,
                  })
                }
              >
                {t('staffNew.confirmYes')}
              </Button>
              <Button disabled={create.isPending} onClick={() => setConfirm(null)}>
                {t('staffNew.confirmNo')}
              </Button>
            </div>
          }
        >
          {confirm.maskedName ? (
            <>
              {t('staffNew.confirmBody')} <bdi>{confirm.maskedName}</bdi>
            </>
          ) : (
            t('staffNew.confirmBodyNoName')
          )}
        </Notice>
      )}
      <FormCard
        onSubmit={submit}
        cta={t('staffNew.cta')}
        ctaDisabled={!ready || confirm !== null}
        busy={create.isPending}
        secondary={{
          label: t('common.cancel'),
          onClick: () => void navigate({ to: '/clinic-setup/staff' }),
        }}
        footNote={t('staffNew.pwNote')}
      >
        <FieldGrid>
          <TextField
            label={t('staffNew.first')}
            value={first}
            onChange={setFirst}
            requirement={{ word: t('signin.required'), kind: 'required' }}
          />
          <TextField
            label={t('staffNew.last')}
            value={last}
            onChange={setLast}
            requirement={{ word: t('signin.required'), kind: 'required' }}
          />
          <TextField
            label={t('staffNew.mobile')}
            value={mobile}
            onChange={changeMobile}
            help={t('staffNew.mobileHelp')}
            requirement={{ word: t('signin.required'), kind: 'required' }}
            machine
            type="tel"
            prefix="+98"
          />
          <TextField
            label={t('staffNew.email')}
            value={email}
            onChange={setEmail}
            help={t('staffNew.emailHelp')}
            requirement={{ word: t('signin.optional'), kind: 'optional' }}
            machine
            type="email"
          />
          <Span2>
            <SelectField
              label={t('staffNew.role')}
              value={roleId}
              onChange={setRoleId}
              options={[
                { value: '', label: '—' },
                ...roles.map((r) => ({ value: r.id, label: r.name })),
              ]}
              help={t('staffNew.roleHelp')}
              requirement={{ word: t('signin.required'), kind: 'required' }}
            />
          </Span2>
        </FieldGrid>
      </FormCard>
    </div>
  );
}

function HandoverPanel(props: { result: AddEmployeeResult; email: string; onDone: () => void }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const r = props.result;
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (key: string, value: string) => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const rows: [string, string][] = [
    ...(r.username ? ([[t('handover.username'), r.username]] as [string, string][]) : []),
    ...(props.email ? ([[t('handover.email'), props.email]] as [string, string][]) : []),
    ...(r.tempPassword ? ([[t('handover.temp'), r.tempPassword]] as [string, string][]) : []),
  ];

  return (
    <div style={{ maxInlineSize: 640, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={a.pageHead} style={{ marginBlockEnd: 0 }}>
        <h1 className={a.pageTitle}>{t('handover.title')}</h1>
        <p className={a.pageSub}>{t('handover.sub')}</p>
      </div>
      {r.created && r.tempPassword ? (
        <>
          <Notice kind="warn" title={t('handover.warnOnce')} role="alert" />
          <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
            {rows.map(([k, v]) => (
              <div key={k} className={a.row}>
                <span className={a.rowKey}>{k}</span>
                <span className={a.rowValue}>
                  <Machine>{v}</Machine>
                </span>
                <span className={a.rowAction}>
                  <Button variant="quiet" onClick={() => copy(k, v)}>
                    {copied === k ? t('handover.copied') : t('handover.copy')}
                  </Button>
                </span>
              </div>
            ))}
          </div>
          <Notice kind="info">{t('handover.firstLogin')}</Notice>
        </>
      ) : (
        <Notice kind="info">{t('staffNew.existing')}</Notice>
      )}
      <div>
        <Button variant="brand" onClick={props.onDone}>
          {t('handover.done')}
        </Button>
      </div>
    </div>
  );
}
